import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import OrderIndexView from './OrderIndexView'
import usePagination from '../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../constants/constant'
import { ORDER_STATUS_OPTIONS } from './constants/orderStatus'
import {
  ORDER_FILTER_KEYS,
  buildAppliedChipLabel,
  buildAppliedDateRange,
  buildSelectValue,
  countSelectedGroups,
  createEmptyDateRange,
  createEmptyDraftFilterSelections,
  dateRangeToPickerValue,
  dateRangeToQuery,
  formatDateRangeSummary,
  getSelectValues,
  normalizeDateRange,
  pickerValueToDateRange
} from './helpers/orderMobileFilters'

const buildUniqueOptionRecords = (items, getValue, getLabel) => {
  const uniqueOptions = []
  const seenValues = new Set()

  items.forEach((item) => {
    const value = getValue(item)
    const normalizedValue = String(value || '').trim()

    if (!normalizedValue || seenValues.has(normalizedValue)) {
      return
    }

    seenValues.add(normalizedValue)
    uniqueOptions.push({
      value: normalizedValue,
      label: getLabel(item)
    })
  })

  return uniqueOptions
}

const OrderIndexApp = () => {
  const [isFetching, setIsFetching] = useState(true)
  const [data, setData] = useState(null)
  const [title, setTitle] = useState('Pedidos')
  const [clientsUid, setClientsUid] = useState([])
  const [filters, setFilters] = useState([])
  const [mobileFilterConfigs, setMobileFilterConfigs] = useState([])
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const { pagination, setPagination, handlePaginationClick, handlePagination } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [labelToIdMapping, setLabelToIdMapping] = useState(new Map())
  const [idToLabelMapping, setIdToLabelMapping] = useState(new Map())
  const [filterVersion, setFilterVersion] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearchInput, setAppliedSearchInput] = useState('')
  const [searchRequestVersion, setSearchRequestVersion] = useState(0)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [draftFilterSelections, setDraftFilterSelections] = useState(
    createEmptyDraftFilterSelections()
  )
  const [draftDateRange, setDraftDateRange] = useState(createEmptyDateRange())

  const abortControllerRef = React.useRef(new AbortController())

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortControllerRef.current.abort('Fetch Aborted by User.')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      abortControllerRef.current.abort('Component unmounted')
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const buildFiltersQuery = useCallback((selectedOptions) => {
    let queryParam = ''

    selectedOptions.forEach((value, key) => {
      if (value.length === 0) return

      switch (key) {
        case ORDER_FILTER_KEYS.client:
          queryParam += `&client_uid=${value.join(',')}`
          break
        case ORDER_FILTER_KEYS.salesman:
          queryParam += `&salesman_uid=${value.join(',')}`
          break
        case ORDER_FILTER_KEYS.status:
          queryParam += `&status=${value.join(',')}`
          break
        default:
          break
      }
    })

    return queryParam
  }, [])

  const buildDraftSelectionsFromApplied = useCallback(
    () => ({
      [ORDER_FILTER_KEYS.client]: buildSelectValue(allSelectedOptions.get(ORDER_FILTER_KEYS.client)),
      [ORDER_FILTER_KEYS.salesman]: buildSelectValue(
        allSelectedOptions.get(ORDER_FILTER_KEYS.salesman)
      ),
      [ORDER_FILTER_KEYS.status]: buildSelectValue(allSelectedOptions.get(ORDER_FILTER_KEYS.status))
    }),
    [allSelectedOptions]
  )

  const syncMobileDraftWithApplied = useCallback(() => {
    setDraftFilterSelections(buildDraftSelectionsFromApplied())
    setDraftDateRange(pickerValueToDateRange(selectedDates))
  }, [buildDraftSelectionsFromApplied, selectedDates])

  const prepareFilterData = useCallback(() => {
    if (!clientsUid) {
      return
    }

    const localLabelToIdMapping = new Map()
    const localIdToLabelMapping = new Map()

    const clientMobileOptions = buildUniqueOptionRecords(
      clientsUid,
      (client) => client.client_db_ref,
      (client) => `${client.client_db_ref} - ${client.client_description}`
    )

    clientMobileOptions.forEach((option) => {
      localLabelToIdMapping.set(option.label, option.value)
      localIdToLabelMapping.set(option.value, option.label)
    })

    const clientOptions = clientMobileOptions.map((option) => option.label)

    const salesmanMobileOptions = buildUniqueOptionRecords(
      clientsUid.filter((client) => client.salesman_name),
      (client) => client.salesman_db_ref,
      (client) => `${client.salesman_db_ref} - ${client.salesman_name}`
    )

    salesmanMobileOptions.forEach((option) => {
      localLabelToIdMapping.set(option.label, option.value)
      localIdToLabelMapping.set(option.value, option.label)
    })

    const salesmanOptions = salesmanMobileOptions.map((option) => option.label)

    const statusOptions = ORDER_STATUS_OPTIONS.map((option) => {
      localLabelToIdMapping.set(option.name, option.value)
      localIdToLabelMapping.set(option.value, option.name)
      return option.name
    })

    const statusMobileOptions = ORDER_STATUS_OPTIONS.map((option) => ({
      label: option.name,
      value: option.value
    }))

    setFilters([
      { key: ORDER_FILTER_KEYS.client, value: clientOptions },
      { key: ORDER_FILTER_KEYS.salesman, value: salesmanOptions },
      { key: ORDER_FILTER_KEYS.status, value: statusOptions }
    ])
    setMobileFilterConfigs([
      {
        key: ORDER_FILTER_KEYS.client,
        title: ORDER_FILTER_KEYS.client,
        options: clientMobileOptions
      },
      {
        key: ORDER_FILTER_KEYS.salesman,
        title: ORDER_FILTER_KEYS.salesman,
        options: salesmanMobileOptions
      },
      {
        key: ORDER_FILTER_KEYS.status,
        title: ORDER_FILTER_KEYS.status,
        options: statusMobileOptions
      }
    ])
    setLabelToIdMapping(localLabelToIdMapping)
    setIdToLabelMapping(localIdToLabelMapping)
  }, [clientsUid])

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    const labels = Array.isArray(selectedLabels)
      ? selectedLabels
      : Array.from(selectedLabels || [])

    const selectedIds = labels
      .map((label) => labelToIdMapping.get(label))
      .filter((value) => value !== undefined)

    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      updatedOptions.set(identifier, selectedIds)
      setFilterQuery(buildFiltersQuery(updatedOptions))
      return updatedOptions
    })

    pagination.current_page = 1
  }

  const handleDateFilter = (datesObj) => {
    const nextDateRange = pickerValueToDateRange(datesObj)
    const appliedDateRange = buildAppliedDateRange(nextDateRange)

    setSelectedDates(dateRangeToPickerValue(appliedDateRange))
    setDateQuery(dateRangeToQuery(appliedDateRange))
    pagination.current_page = 1
  }

  const handleOpenMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(true)
  }

  const handleCloseMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(false)
  }

  const handleDraftSelectChange = (filterKey, nextValue) => {
    setDraftFilterSelections((prevValue) => ({
      ...prevValue,
      [filterKey]: buildSelectValue(getSelectValues(nextValue))
    }))
  }

  const handleDraftDateChange = (boundary, nextValue) => {
    const normalizedValue = nextValue?.isValid?.() ? nextValue.startOf('day') : null

    setDraftDateRange((prevRange) =>
      normalizeDateRange({
        ...prevRange,
        [boundary]: normalizedValue
      })
    )
  }

  const clearDraftDateRange = () => {
    setDraftDateRange(createEmptyDateRange())
  }

  const clearSearchInput = () => {
    setSearchInput('')
    setAppliedSearchInput('')
    setSearchRequestVersion((prev) => prev + 1)
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
  }

  const resetReactFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setDateQuery(null)
    setSelectedDates(null)
    setSearchInput('')
    setAppliedSearchInput('')
    setSearchRequestVersion((prev) => prev + 1)
    setDraftFilterSelections(createEmptyDraftFilterSelections())
    setDraftDateRange(createEmptyDateRange())
    setFilterVersion((prevVersion) => prevVersion + 1)
  }

  const clearAllMobileFilters = () => {
    resetReactFilters()
    setIsMobileFiltersOpen(false)
  }

  const applyMobileFilters = () => {
    const nextSelectedFilterOptions = new Map()
    const nextDateRange = buildAppliedDateRange(draftDateRange)

    Object.values(ORDER_FILTER_KEYS).forEach((filterKey) => {
      nextSelectedFilterOptions.set(filterKey, [...getSelectValues(draftFilterSelections[filterKey])])
    })

    setAllSelectedOptions(nextSelectedFilterOptions)
    setFilterQuery(buildFiltersQuery(nextSelectedFilterOptions))
    setSelectedDates(dateRangeToPickerValue(nextDateRange))
    setDateQuery(dateRangeToQuery(nextDateRange))
    pagination.current_page = 1
    setIsMobileFiltersOpen(false)
  }

  useEffect(() => {
    prepareFilterData()
  }, [prepareFilterData])

  useEffect(() => {
    async function fetchAll() {
      await fetchClientsFromServer()
    }
    fetchAll()
  }, [])

  const selectedDateRange = useMemo(() => pickerValueToDateRange(selectedDates), [selectedDates])

  const appliedFilterSelections = useMemo(
    () => ({
      [ORDER_FILTER_KEYS.client]: buildSelectValue(allSelectedOptions.get(ORDER_FILTER_KEYS.client)),
      [ORDER_FILTER_KEYS.salesman]: buildSelectValue(
        allSelectedOptions.get(ORDER_FILTER_KEYS.salesman)
      ),
      [ORDER_FILTER_KEYS.status]: buildSelectValue(allSelectedOptions.get(ORDER_FILTER_KEYS.status))
    }),
    [allSelectedOptions]
  )

  const appliedFilterGroupCount = useMemo(
    () => countSelectedGroups(appliedFilterSelections, selectedDateRange),
    [appliedFilterSelections, selectedDateRange]
  )

  const appliedFilterChips = useMemo(() => {
    const chips = []
    const clientValues = getSelectValues(appliedFilterSelections[ORDER_FILTER_KEYS.client])
    const salesmanValues = getSelectValues(appliedFilterSelections[ORDER_FILTER_KEYS.salesman])
    const statusValues = getSelectValues(appliedFilterSelections[ORDER_FILTER_KEYS.status])
    const appliedDateRange = buildAppliedDateRange(selectedDateRange)

    if (clientValues.length > 0) {
      chips.push({
        key: ORDER_FILTER_KEYS.client,
        label: buildAppliedChipLabel({
          singularLabel: 'Cliente',
          pluralLabel: 'Clientes',
          values: clientValues,
          idToLabelMapping
        })
      })
    }

    if (salesmanValues.length > 0) {
      chips.push({
        key: ORDER_FILTER_KEYS.salesman,
        label: buildAppliedChipLabel({
          singularLabel: 'Creado por',
          pluralLabel: 'Creados por',
          values: salesmanValues,
          idToLabelMapping
        })
      })
    }

    if (statusValues.length > 0) {
      chips.push({
        key: ORDER_FILTER_KEYS.status,
        label: buildAppliedChipLabel({
          singularLabel: 'Estado',
          pluralLabel: 'Estados',
          values: statusValues,
          idToLabelMapping
        })
      })
    }

    if (appliedDateRange.start) {
      chips.push({
        key: 'date-range',
        label: `Fechas: ${formatDateRangeSummary(appliedDateRange, '')}`
      })
    }

    return chips
  }, [appliedFilterSelections, idToLabelMapping, selectedDateRange])

  const isAnyFilterSelected = () => {
    return (
      Array.from(allSelectedOptions.values()).some((options) => options.length > 0) ||
      dateQuery != null
    )
  }

  const fetchClientsFromServer = async (loadingState = true) => {
    if (loadingState) {
      setFetchDataStatus((prevStatus) => ({ ...prevStatus, filters: true }))
    }
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/transactions?client_names_only=true`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )
      if (response.status === 401) {
        console.error('Authorization error', response)
        return
      }

      const responseData = await response.json()
      setClientsUid(responseData.result)
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    } finally {
      setFetchDataStatus((prevStatus) => ({ ...prevStatus, filters: false }))
    }
  }

  const fetchDataFromServer = useCallback(async () => {
    const per_page = pagination.per_page
    const page = pagination.current_page

    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/transactions?page=${page}&per_page=${per_page}${filterQuery || dateQuery || appliedSearchInput ? '&filter_search=true' : ''}${filterQuery ? filterQuery : ''}${dateQuery ? dateQuery : ''}${appliedSearchInput ? `&q=${appliedSearchInput}` : ''}`,
      {
        method: 'GET',
        signal: abortControllerRef.current.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    )

    if (response.status === 401) {
      return console.error(response)
    }

    setPagination((prev) => ({
      ...prev,
      total_pages: Math.ceil(response.count / prev.per_page)
    }))
    setData(response.table)
    setIsFetching(false)
  }, [pagination.current_page, pagination.per_page, filterQuery, dateQuery, appliedSearchInput, setPagination])

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
    }

    fetchAll()
  }, [pagination.current_page, filterQuery, dateQuery, appliedSearchInput, searchRequestVersion, fetchDataFromServer])

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      setAppliedSearchInput(searchInput)
      setSearchRequestVersion((prev) => prev + 1)
      setPagination((prev) => ({
        ...prev,
        current_page: 1
      }))
    }
  }

  return (
    <OrderIndexView
      fetchDataStatus={fetchDataStatus}
      data={data}
      setFilterQuery={setFilterQuery}
      filterQuery={filterQuery}
      searchQuery={searchInput}
      pagination={pagination}
      handlePaginationClick={handlePaginationClick}
      handlePagination={handlePagination}
      filters={filters}
      mobileFilterConfigs={mobileFilterConfigs}
      updateAllSelectedOptions={updateAllSelectedOptions}
      resetReactFilters={resetReactFilters}
      filterVersion={filterVersion}
      isAnyFilterSelected={isAnyFilterSelected()}
      isFetching={isFetching}
      title={title}
      searchInput={searchInput}
      handleSearch={handleSearch}
      handleEnterSearch={handleEnterSearch}
      handleDateFilter={handleDateFilter}
      selectedDates={selectedDates}
      clearSearch={clearSearchInput}
      isMobileFiltersOpen={isMobileFiltersOpen}
      openMobileFilters={handleOpenMobileFilters}
      closeMobileFilters={handleCloseMobileFilters}
      appliedFilterGroupCount={appliedFilterGroupCount}
      appliedFilterChips={appliedFilterChips}
      draftFilterSelections={draftFilterSelections}
      handleDraftSelectChange={handleDraftSelectChange}
      draftDateRange={draftDateRange}
      handleDraftDateChange={handleDraftDateChange}
      clearDraftDateRange={clearDraftDateRange}
      clearAllMobileFilters={clearAllMobileFilters}
      applyMobileFilters={applyMobileFilters}
    />
  )
}

export default OrderIndexApp
